<i18n src="@/locales/models/harvested_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/harvested_package.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/packing_house/process/manage_harvested_batch/new_harvested_batch.yml"
  lang="yaml"
></i18n>

<template>
  <div>
    <go-back-link
      :text="$t('labels.go_back_link')"
      name="ph_process_harvested_batches_list"
      :params="{
        packing_house_process_id: packing_house_process_id
      }"
    />
    <bread-crumbs
      :labels="[
        module_name,
        packing_house_process.name,
        $t('labels.harvested_batches_title')
      ]"
    ></bread-crumbs>

    <alert-danger :errors_data="std_err" />

    <div class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>

        <div class="panel-body">
          <div class="space-y-8 divide-y divide-slate-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.batch_code") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="batch_code"
                    :class="['input-text', isInvalid('batch_code')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.registered_at") }}
                </label>
                <div class="mt-1">
                  <date-form v-if="render_registered_at" v-model="registered_at" />
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.producer") }}
                </label>
                <div class="mt-1">
                  <v-select
                    v-if="producers_organizations?.length > 0"
                    name="producer_or_organization"
                    class="custom-tailwind-select"
                    v-model="producer_organization_selected"
                    :options="producers_organizations"
                  />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.crop_field") }}
                </label>
                <div class="mt-1">
                  <v-select
                    v-if="crop_field_options.length > 0"
                    name="crop_field"
                    class="custom-tailwind-select"
                    v-model="crop_field_selected"
                    :options="crop_field_options"
                  />
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 pt-6">
              <template
                v-for="(crop_har_package, index) in crop_har_packages"
                :key="crop_har_package.id"
              >
                <div class="sm:col-span-3">
                  <label class="label-form">{{
                    $t("model.harvested_package.crop_har_package")
                  }}</label>
                  <div class="mt-1">
                    <div class="content-data">
                      {{ crop_har_package.name }}
                    </div>
                  </div>
                </div>
                <div class="sm:col-span-3">
                  <label class="label-form">
                    {{ $t("model.harvested_package.number") }}
                  </label>
                  <div class="flex mt-1">
                    <input
                      type="text"
                      v-model.number.trim="harvested_packages[index].number"
                      :class="['input-text', isInvalid('number')]"
                    />
                  </div>
                </div>
              </template>
            </div>
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 pt-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.total_weight") }}
                </label>
                <div class="flex mt-1">
                  <input
                    type="text"
                    v-model.number.trim="total_weight"
                    :class="['input-text', isInvalid('total_weight')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.discarded_weight") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.number.trim="discarded_weight"
                    :class="['input-text', isInvalid('discarded_weight')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.ok_weight") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.number.trim="ok_weight"
                    :class="['input-text', isInvalid('ok_weight')]"
                  />
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 pt-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.crop_type") }}
                </label>
                <div class="mt-1">
                  <select-form
                    v-if="crop_types.length > 1"
                    :options="crop_types"
                    v-model="crop_type_id"
                  />
                  <div class="content-data" v-else-if="crop_types.length == 1">
                    {{ crop_types[0].name }}
                  </div>
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.crop_variety") }}
                </label>
                <div class="mt-1">
                  <select-form
                    v-if="crop_varieties.length > 1"
                    :options="crop_varieties"
                    v-model="crop_variety_id"
                  />
                  <div
                    class="content-data"
                    v-else-if="crop_varieties.length == 1"
                  >
                    {{ crop_varieties[0].name }}
                  </div>
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvested_batch.agro_exporter") }}
                </label>
                <div class="mt-1">
                  <select-form
                    v-if="agro_exporters.length > 1"
                    :options="agro_exporters"
                    v-model="agro_exporter_id"
                  />
                  <div
                    class="content-data"
                    v-else-if="agro_exporters.length == 1"
                  >
                    {{ agro_exporters[0].name }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="panel-footer">
          <div class="panel-footer-actions">
            <white-button @click.prevent="goToListHarvestedBatches()" :label='$t("labels.cancel_button")' />
            <span class="space-actions"></span>
            <success-button v-if="!charging_submit" @click.prevent="create()" :label="$t('labels.save_button')" />
            <success-button-processing v-else :label="$t('labels.save_button')" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RequesterQuery from "@/views/packing_house/requesters/process/harvested_batches/query.js";
import Requester from "@/views/packing_house/requesters/process/harvested_batches/manage_harvested_batch.js";
import BreadCrumbs from "@/components/views/layout/BreadCrumbs.vue";

import moment from "moment";

export default {
  name: "NewHarvestedBatch",
  components: { BreadCrumbs },
  data() {
    return {
      charging_submit: false,
      packing_house_process: {},
      batch_code: "",
      total_weight: null,
      discarded_weight: null,
      ok_weight: null,
      registered_at: null,
      render_registered_at: false,
      crop_type_id: null,
      crop_variety_id: null,
      producer_organization_selected: null,
      crop_field_selected: null,
      agro_exporter_id: null,
      crop_types: [],
      crop_varieties: [],
      agro_exporters: [],
      producers_organizations: [],
      crop_har_packages: [],
      harvested_packages: []
    };
  },
  props: {
    packing_house_process_id: {
      required: true
    },
    packing_house_id: {
      required: true
    }
  },
  created() {
    this.checkIfAuthenticated().then(() => {
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          packing_house_id: this.packing_house_id
        }
      }).then(() => {
        this.registered_at = moment().format('YYYY-MM-DD');
        this.render_registered_at = true;
        this.fetchPackingHouseProcess();
        this.fetchCropTypes();
        this.fetchCropVarieties();
        this.fetchAgroExporters();
        this.fetchProducersOrganizations();
        this.fetchCropHarPackages();
      });
    });
  },
  computed: {
    crop_field_options: function () {
      if (!this.producer_organization_selected) return [];

      return this.producer_organization_selected.crop_fields;
    },
    harvested_packages_params: function () {
      return this.harvested_packages.filter(har_package => Number(har_package.number) > 0);
    },
    validate: function () {
      return this.harvested_packages
        .map(
          harvested_package =>
            Boolean(harvested_package.crop_har_package_id) &&
            Number(harvested_package.number) > 0
        )
        .includes(true);
    }
  },
  watch: {
    crop_field_options: function (opts) {
      let size = opts.length;
      if (size == 0) this.crop_field_selected = null;
      if (size == 1) this.crop_field_selected = opts[0];
    },
  },
  methods: {
    fetchPackingHouseProcess: async function () {
      const response = await Requester.fetchPackingHouseProcess(
        this.packing_house_process_id
      );
      this.packing_house_process = response.data;
    },
    fetchCropTypes: async function () {
      const response = await RequesterQuery.fetchCropTypes(
        this.packing_house_process_id
      );
      this.crop_types = response.data;
      this.crop_type_id = this.hasOneItem(this.crop_types);
    },
    fetchCropVarieties: async function () {
      const response = await RequesterQuery.fetchCropVarieties(
        this.packing_house_process_id
      );
      this.crop_varieties = response.data;
      this.crop_variety_id = this.hasOneItem(this.crop_varieties);
    },
    fetchAgroExporters: async function () {
      const response = await RequesterQuery.fetchAgroExporters(
        this.packing_house_process_id
      );
      this.agro_exporters = response.data;
      this.agro_exporter_id = this.hasOneItem(this.agro_exporters);
    },
    fetchProducersOrganizations: async function () {
      const response = await RequesterQuery.fetchProducersOrganizations(
        this.packing_house_process_id
      );
      this.producers_organizations = response.data;
    },
    fetchCropHarPackages: async function () {
      const response = await RequesterQuery.fetchCropHarPackages(
        this.packing_house_process_id
      );
      this.crop_har_packages = response.data;
      this.harvested_packages = this.crop_har_packages.map(item => {
        return {
          id: null,
          crop_har_package_id: item.id,
          number: null
        };
      });
    },
    create: async function () {
      this.charging_submit = true;
      try {
        let response = await Requester.create(this.packing_house_process_id, {
          batch_code: this.batch_code,
          total_weight: this.total_weight,
          discarded_weight: this.discarded_weight,
          ok_weight: this.ok_weight,
          registered_at: this.registered_at,
          crop_type_id: this.crop_type_id,
          crop_variety_id: this.crop_variety_id,
          producer_id: this.producer_organization_selected?.producer_id,
          organization_id: this.producer_organization_selected?.organization_id,
          crop_field_id: this.crop_field_selected?.id,
          agro_exporter_id: this.agro_exporter_id,
          harvested_packages: this.harvested_packages_params
        });

        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToListHarvestedBatches();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.charging_submit = false;
    },
    goToListHarvestedBatches: function () {
      this.$router.push({
        name: "ph_process_harvested_batches_list",
        params: {
          packing_house_process_id: this.packing_house_process_id
        }
      });
    },
    hasOneItem: function (array) {
      return array.length == 1 ? array[0].id : null;
    }
  }
};
</script>
